import is from '@sindresorhus/is';
import { type FunctionComponent, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { Translate } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { Input } from '~/ui/core/input/Input';
import { Textarea } from '~/ui/core/textarea/Textarea';

import type { GrowthGoal, GrowthGoalDetail, GrowthOutcomeResult } from '../../data-contract/growth';
import { useGoalAction } from '../api/growth-mutations';
import { ChoiceButtons } from '../components/ChoiceButtons';
import { DetailSection } from '../components/GrowthTileFrame';
import { type GrowthTone, StatusDot } from '../components/goal-display';

const outcomeTones: Record<GrowthOutcomeResult, GrowthTone> = {
    dropped: 'muted',
    partly: 'orange',
    reached: 'success',
};

const OUTCOME_ORDER: readonly GrowthOutcomeResult[] = ['dropped', 'partly', 'reached'];

const outcomeLabel = (result: GrowthOutcomeResult, translate: Translate): string => {
    switch (result) {
        case 'dropped':
            return translate('pages.growth.outcomes.dropped');
        case 'partly':
            return translate('pages.growth.outcomes.partly');
        case 'reached':
            return translate('pages.growth.outcomes.reached');
    }
};

const ProposeOutcome: FunctionComponent<{ readonly goal: GrowthGoal }> = ({ goal }) => {
    const translate = useTranslate();
    const goalAction = useGoalAction(goal.id);
    const [result, setResult] = useState<GrowthOutcomeResult>();
    const [note, setNote] = useState('');
    const [due, setDue] = useState('');

    return (
        <div className="flex flex-col gap-xxl">
            <ChoiceButtons<GrowthOutcomeResult>
                ariaLabel={translate('pages.growth.detail.outcome-title')}
                options={OUTCOME_ORDER.map((value) => ({ label: outcomeLabel(value, translate), value }))}
                value={result}
                onChange={setResult}
            />
            <Textarea
                aria-label={translate('pages.growth.detail.outcome-title')}
                autoResize={true}
                placeholder={translate('pages.growth.detail.outcome-note-placeholder')}
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
            />
            <div className="flex flex-wrap items-center justify-between gap-m">
                <div className="flex items-center gap-s">
                    <Input
                        aria-label={translate('pages.growth.form.due-label')}
                        className="w-[10rem]"
                        type="date"
                        value={due}
                        onChange={(event) => setDue(event.target.value)}
                    />
                    <Button
                        disabled={due.length === 0 || goalAction.isPending}
                        size="sm"
                        variant="tertiary"
                        onClick={() => goalAction.mutate({ action: 'extend', due })}
                    >
                        {translate('pages.growth.actions.extend')}
                    </Button>
                </div>
                <Button
                    disabled={result === undefined || goalAction.isPending}
                    size="sm"
                    variant="primary"
                    onClick={() => result && goalAction.mutate({ action: 'propose-outcome', note, result })}
                >
                    {translate('pages.growth.actions.propose-outcome')}
                </Button>
            </div>
        </div>
    );
};

export const ReviewSection: FunctionComponent<{
    readonly goal: GrowthGoal;
    readonly viewerRole: GrowthGoalDetail['viewerRole'];
}> = ({ goal, viewerRole }) => {
    const translate = useTranslate();
    const { outcome } = goal;

    if (goal.state !== 'in-review' && goal.state !== 'done') {
        return null;
    }

    return (
        <DetailSection title={translate('pages.growth.detail.outcome-title')}>
            {is.nullOrUndefined(outcome) ? (
                viewerRole === 'owner' ? (
                    <ProposeOutcome goal={goal} />
                ) : (
                    <p className="m-0 text-body-s text-text-muted">{translate('pages.growth.states.in-review')}</p>
                )
            ) : (
                <div className="flex flex-col gap-xs rounded-xl border border-border-subtle px-xl py-l">
                    <StatusDot label={outcomeLabel(outcome.result, translate)} tone={outcomeTones[outcome.result]} />
                    {is.nonEmptyString(outcome.note) ? (
                        <span className="text-body-s text-text-secondary">{outcome.note}</span>
                    ) : null}
                </div>
            )}
        </DetailSection>
    );
};
